import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    LinearProgress,
    Chip,
    Stack,
    Alert,
    useTheme,
    alpha,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Calculate as CalculateIcon,
    Assignment as AssignmentIcon,
    Science as ScienceIcon,
    LocalDrink as LocalDrinkIcon,
    Autorenew as AutorenewIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { getAllUnits } from '../../../../utils/unitConversions';
import { usePermissions } from '../../../../utils/permissions';

export default function YieldSummary({ formulationUnit, onDataUpdate, batchId, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const { canAccessPage, canEditPage } = usePermissions();
    const defaultUnit = 'gal';
    const availableUnits = getAllUnits();
    
    const [formData, setFormData] = useState({
        theoretical_yield_kg: '',
        theoretical_yield_unit: defaultUnit,
        actual_yield_kg: '',
        actual_yield_unit: defaultUnit,
        waste_amount_kg: '',
        waste_amount_unit: defaultUnit,
        loss_reason: '',
        final_ph: '',
        final_viscosity: '',
        final_color: '',
        final_texture: '',
        quality_notes: '',
        yield_comments: '',
        
        // Yield-Specific Performance
        end_time: null,
        right_first_time: true,
        
        // Yield Recovery & Analysis
        recoverable_waste_kg: '',
        recoverable_waste_unit: defaultUnit,
        unrecoverable_waste_kg: '',
        unrecoverable_waste_unit: defaultUnit,
        yield_loss_category: ''
    });

    const [currentYieldSummaryId, setCurrentYieldSummaryId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [autoFilling, setAutoFilling] = useState(false);

    useEffect(() => {
        if (batchId) {
            loadYieldSummaryData();
        }
    }, [batchId]);

    // Check completion status whenever formData changes
    useEffect(() => {
        checkYieldCompletion();
    }, [formData]);

    // Check completion and notify parent
    const checkYieldCompletion = () => {
        const hasValue = (value) => value !== '' && value !== null && value !== undefined;
        
        const isComplete = !!(
            hasValue(formData.theoretical_yield_kg) && 
            hasValue(formData.actual_yield_kg) && 
            hasValue(formData.waste_amount_kg) &&
            hasValue(formData.final_ph) && 
            hasValue(formData.final_viscosity) &&
            hasValue(formData.final_color) &&
            hasValue(formData.final_texture) &&
            hasValue(formData.end_time) &&
            hasValue(formData.recoverable_waste_kg) &&
            hasValue(formData.unrecoverable_waste_kg)
        );
        
        if (onDataUpdate) {
            onDataUpdate();
        }
        
        return isComplete;
    };

    // Helper function to handle 0 values properly
    const getValueOrEmpty = (value) => {
        return value !== null && value !== undefined ? value : '';
    };

    const loadYieldSummaryData = async () => {
        try {
            setLoading(true);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user || !batchId) return;

            // Fetch batch planned completion for fallback
            const { data: batchRow } = await supabase
                .from('production_batches')
                .select('planned_completion_date, planned_completion_time, planned_start_date, planned_start_time')
                .eq('id', batchId)
                .single();

            const getPlannedCompletion = () => {
                if (!batchRow?.planned_completion_date) return null;
                const str = batchRow.planned_completion_time
                    ? `${batchRow.planned_completion_date} ${batchRow.planned_completion_time}`
                    : batchRow.planned_completion_date;
                return dayjs(str).isValid() ? dayjs(str) : null;
            };

            const { data, error } = await supabase
                .from('yield_summary')
                .select('*')
                .eq('batch_id', batchId)
                .single();

            if (data && !error) {
                const batchUnit = formulationUnit || 'gal';
                setFormData({
                    theoretical_yield_kg: getValueOrEmpty(data.theoretical_yield_kg),
                    theoretical_yield_unit: data.theoretical_yield_unit || batchUnit,
                    actual_yield_kg: getValueOrEmpty(data.actual_yield_kg),
                    actual_yield_unit: data.actual_yield_unit || batchUnit,
                    waste_amount_kg: getValueOrEmpty(data.waste_amount_kg),
                    waste_amount_unit: data.waste_amount_unit || batchUnit,
                    loss_reason: data.loss_reason || '',
                    final_ph: getValueOrEmpty(data.final_ph),
                    final_viscosity: getValueOrEmpty(data.final_viscosity),
                    final_color: data.final_color || '',
                    final_texture: data.final_texture || '',
                    quality_notes: data.quality_notes || '',
                    yield_comments: data.yield_comments || '',
                    
                    // Yield-Specific Performance
                    end_time: data.end_time ? dayjs(data.end_time) : getPlannedCompletion(),
                    right_first_time: data.right_first_time !== undefined ? data.right_first_time : true,
                    
                    // Yield Recovery & Analysis
                    recoverable_waste_kg: getValueOrEmpty(data.recoverable_waste_kg),
                    recoverable_waste_unit: data.recoverable_waste_unit || batchUnit,
                    unrecoverable_waste_kg: getValueOrEmpty(data.unrecoverable_waste_kg),
                    unrecoverable_waste_unit: data.unrecoverable_waste_unit || batchUnit,
                    yield_loss_category: data.yield_loss_category || ''
                });
                setCurrentYieldSummaryId(data.id);
            } else {
                // No saved yield summary — pre-fill end_time from planned completion
                const plannedEnd = getPlannedCompletion();
                if (plannedEnd) {
                    setFormData(prev => ({ ...prev, end_time: plannedEnd }));
                }
            }
        } catch (error) {
            console.error('Error loading yield summary data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAutoFill = async () => {
        try {
            setAutoFilling(true);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user || !batchId) return;

            // Fetch batch data to get calculated production volume
            const { data: batchData, error: batchError } = await supabase
                .from('production_batches')
                .select('calculated_production_volume, calculated_production_unit')
                .eq('id', batchId)
                .single();

            if (batchError) {
                console.error('Error fetching batch data:', batchError);
                return;
            }

            if (batchData && batchData.calculated_production_volume) {
                const theoreticalYield = batchData.calculated_production_volume;
                
                // Auto-fill with ideal values (everything went well)
                setFormData(prev => ({
                    ...prev,
                    theoretical_yield_kg: theoreticalYield,
                    theoretical_yield_unit: 'gal',
                    actual_yield_kg: theoreticalYield,
                    actual_yield_unit: 'gal',
                    waste_amount_kg: 0,
                    waste_amount_unit: 'gal',
                    recoverable_waste_kg: 0,
                    recoverable_waste_unit: 'gal',
                    unrecoverable_waste_kg: 0,
                    unrecoverable_waste_unit: 'gal',
                    right_first_time: true
                }));
            } else {
                console.warn('No calculated production volume found for this batch');
            }
        } catch (error) {
            console.error('Error auto-filling yield data:', error);
        } finally {
            setAutoFilling(false);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                console.error('No authenticated user found');
                return;
            }

            // Convert empty strings to null for numeric fields (preserve 0 values)
            const toNumericOrNull = (value) => value === '' ? null : value;
            
            const saveData = {
                ...formData,
                // Numeric fields - convert empty strings to null but keep 0
                theoretical_yield_kg: toNumericOrNull(formData.theoretical_yield_kg),
                actual_yield_kg: toNumericOrNull(formData.actual_yield_kg),
                waste_amount_kg: toNumericOrNull(formData.waste_amount_kg),
                final_ph: toNumericOrNull(formData.final_ph),
                final_viscosity: toNumericOrNull(formData.final_viscosity),
                end_time: formData.end_time?.toISOString(),
                recoverable_waste_kg: toNumericOrNull(formData.recoverable_waste_kg),
                unrecoverable_waste_kg: toNumericOrNull(formData.unrecoverable_waste_kg),
                yield_percentage: calculateYieldPercentage(),
                user_id: user.id,
                batch_id: batchId,
                updated_at: new Date().toISOString()
            };

            let result;
            if (currentYieldSummaryId) {
                // Update existing record
                const { data, error } = await supabase
                    .from('yield_summary')
                    .update(saveData)
                    .eq('id', currentYieldSummaryId)
                    .select()
                    .single();
                
                if (error) {
                    console.error('Error updating yield summary:', error);
                    return;
                }
                result = data;
            } else {
                // Create new record
                const { data, error } = await supabase
                    .from('yield_summary')
                    .insert([saveData])
                    .select()
                    .single();
                
                if (error) {
                    console.error('Error creating yield summary:', error);
                    return;
                }
                result = data;
                setCurrentYieldSummaryId(data.id);
            }
            
            // Check completion and notify parent after successful save
            checkYieldCompletion();
        } catch (error) {
            console.error('Error saving yield summary:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateYieldPercentage = () => {
        const theoretical = parseFloat(formData.theoretical_yield_kg) || 0;
        const actual = parseFloat(formData.actual_yield_kg) || 0;
        
        if (theoretical > 0) {
            return Math.round((actual / theoretical) * 100);
        }
        return 0;
    };

    const calculateWastePercentage = () => {
        const theoretical = parseFloat(formData.theoretical_yield_kg) || 0;
        const waste = parseFloat(formData.waste_amount_kg) || 0;
        
        if (theoretical > 0) {
            return Math.round((waste / theoretical) * 100);
        }
        return 0;
    };

    const getYieldStatus = () => {
        const percentage = calculateYieldPercentage();
        if (percentage >= 95) return { status: 'excellent', color: '#4caf50', icon: <TrendingUpIcon /> };
        if (percentage >= 85) return { status: 'good', color: '#8bc34a', icon: <TrendingUpIcon /> };
        if (percentage >= 75) return { status: 'acceptable', color: '#ff9800', icon: <WarningIcon /> };
        return { status: 'poor', color: '#f44336', icon: <TrendingDownIcon /> };
    };

    const calculateYieldEfficiency = () => {
        const theoretical = parseFloat(formData.theoretical_yield_kg) || 0;
        const actual = parseFloat(formData.actual_yield_kg) || 0;
        const waste = parseFloat(formData.waste_amount_kg) || 0;
        // Note: end_time no longer used for efficiency calculation
        
        if (theoretical === 0) return 0;
        
        // Base yield efficiency
        const baseYield = (actual / theoretical) * 100;
        
        // Factor in waste efficiency (less waste = better efficiency)
        const wasteEfficiency = waste === 0 ? 100 : Math.max(0, 100 - ((waste / theoretical) * 100));
        
        // Factor in right-first-time bonus
        const rftBonus = formData.right_first_time ? 5 : 0;
        
        // Combined efficiency score
        const efficiency = Math.min(100, (baseYield * 0.7) + (wasteEfficiency * 0.25) + rftBonus);
        
        return Math.round(efficiency * 10) / 10; // Round to 1 decimal
    };

    const yieldPercentage = calculateYieldPercentage();
    const wastePercentage = calculateWastePercentage();
    const yieldStatus = getYieldStatus();
    const efficiency = Math.max(0, 100 - wastePercentage);
    const canEdit = canEditPage('batch_logs_yield');

    // Permission check for batch logs yield
    if (!canAccessPage('batch_logs_yield')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                px: 3
            }}>
                <Card sx={{
                    p: 8,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 600,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 6,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <TrendingUpIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        mb: 3, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2.5rem',
                        letterSpacing: '-0.8px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 4,
                        fontWeight: 500,
                        fontSize: '1.2rem'
                    }}>
                        Yield Summary Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        maxWidth: 450,
                        mx: 'auto'
                    }}>
                        You don't currently have the necessary permissions to access the Yield Summary. 
                        Contact your system administrator to request access or verify your role permissions.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
            <Grid container spacing={3}>
                {/* Key Metrics Widgets */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        height: 180,
                        display: 'flex',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
                            <Box sx={{ color: yieldStatus.color, mb: 1 }}>
                                {yieldStatus.icon}
                            </Box>
                            <Typography variant="h1" sx={{ 
                                fontWeight: 500, 
                                color: yieldStatus.color,
                                mb: 1,
                                lineHeight: 1
                            }}>
                                {yieldPercentage}%
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                                YIELD
                            </Typography>
                            <Typography variant="caption" sx={{ 
                                fontWeight: 500, 
                                color: yieldStatus.color,
                                textTransform: 'uppercase'
                            }}>
                                {yieldStatus.status}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: formData.actual_yield_kg ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                            <LocalDrinkIcon sx={{ 
                                fontSize: 40, 
                                color: formData.actual_yield_kg ? theme.palette.success.main : theme.palette.text.disabled,
                                mb: 1 
                            }} />
                            <Typography variant="h2" sx={{ 
                                fontWeight: 500, 
                                color: formData.actual_yield_kg ? theme.palette.success.main : theme.palette.text.disabled,
                                mb: 1
                            }}>
                                {formData.actual_yield_kg || '—'}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                ACTUAL YIELD ({(formulationUnit || 'gal').toUpperCase()})
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: wastePercentage > 10 ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.success.main, 0.1),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                            <WarningIcon sx={{ 
                                fontSize: 40, 
                                color: wastePercentage > 10 ? theme.palette.error.main : theme.palette.success.main,
                                mb: 1 
                            }} />
                            <Typography variant="h2" sx={{ 
                                fontWeight: 500, 
                                color: wastePercentage > 10 ? theme.palette.error.main : theme.palette.success.main,
                                mb: 1
                            }}>
                                {wastePercentage}%
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                WASTE
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: efficiency >= 90 ? alpha(theme.palette.success.main, 0.1) : efficiency >= 80 ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                            <CalculateIcon sx={{ 
                                fontSize: 40, 
                                color: efficiency >= 90 ? theme.palette.success.main : efficiency >= 80 ? theme.palette.warning.main : theme.palette.error.main,
                                mb: 1 
                            }} />
                            <Typography variant="h2" sx={{ 
                                fontWeight: 500, 
                                color: efficiency >= 90 ? theme.palette.success.main : efficiency >= 80 ? theme.palette.warning.main : theme.palette.error.main,
                                mb: 1
                            }}>
                                {efficiency}%
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                EFFICIENCY
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Yield Progress Visualization */}
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <TrendingUpIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Yield Analysis
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Production efficiency and waste analysis
                                    </Typography>
                                </Box>
                                <Chip 
                                    label={`${yieldPercentage}% Yield`}
                                    sx={{
                                        backgroundColor: yieldStatus.color,
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '1rem',
                                        px: 2,
                                        py: 1
                                    }}
                                />
                            </Box>

                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Yield Progress
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: yieldStatus.color }}>
                                        {yieldPercentage}%
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={Math.min(yieldPercentage, 100)} 
                                    sx={{
                                        height: 12,
                                        borderRadius: 6,
                                        backgroundColor: '#e0e0e0',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: yieldStatus.color,
                                            borderRadius: 6
                                        }
                                    }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">0%</Typography>
                                    <Typography variant="caption" color="text.secondary">Target: 95%</Typography>
                                    <Typography variant="caption" color="text.secondary">100%</Typography>
                                </Box>
                            </Box>

                            {yieldPercentage < 85 && (
                                <Alert 
                                    severity="warning" 
                                    sx={{ 
                                        mb: 3,
                                        border: '1px solid #ff9800',
                                        borderRadius: 2
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Yield below target (95%). Consider reviewing process parameters and documenting any deviations.
                                    </Typography>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Yield Input Card */}
                <Grid item xs={12} lg={6} sx={{ mt: 2 }}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <CalculateIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Yield Calculations
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Theoretical vs actual production amounts
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AutorenewIcon />}
                                    onClick={handleAutoFill}
                                    disabled={!canEdit || autoFilling}
                                    sx={{
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1,
                                        borderColor: theme.palette.primary.main,
                                        color: theme.palette.primary.main,
                                        fontWeight: 500,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.dark,
                                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                        }
                                    }}
                                >
                                    {autoFilling ? 'Auto-filling...' : 'Auto-fill (Perfect Batch)'}
                                </Button>
                            </Box>
                            
                            <Stack spacing={2.5}>
                                <Grid container spacing={1}>
                                    <Grid item xs={8}>
                                        <TextField
                                            fullWidth
                                            label="Theoretical Yield"
                                            type="number"
                                            value={formData.theoretical_yield_kg}
                                            onChange={(e) => setFormData({...formData, theoretical_yield_kg: e.target.value})}
                                            disabled={!canEdit}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Unit</InputLabel>
                                            <Select
                                                value={formData.theoretical_yield_unit}
                                                label="Unit"
                                                onChange={(e) => setFormData({...formData, theoretical_yield_unit: e.target.value})}
                                                disabled={!canEdit}
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }}
                                            >
                                                {availableUnits.map((unit) => (
                                                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                
                                <Grid container spacing={1}>
                                    <Grid item xs={8}>
                                        <TextField
                                            fullWidth
                                            label="Actual Yield"
                                            type="number"
                                            value={formData.actual_yield_kg}
                                            onChange={(e) => setFormData({...formData, actual_yield_kg: e.target.value})}
                                            disabled={!canEdit}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Unit</InputLabel>
                                            <Select
                                                value={formData.actual_yield_unit}
                                                label="Unit"
                                                onChange={(e) => setFormData({...formData, actual_yield_unit: e.target.value})}
                                                disabled={!canEdit}
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }}
                                            >
                                                {availableUnits.map((unit) => (
                                                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                
                                <Grid container spacing={1}>
                                    <Grid item xs={8}>
                                        <TextField
                                            fullWidth
                                            label="Waste Amount"
                                            type="number"
                                            value={formData.waste_amount_kg}
                                            onChange={(e) => setFormData({...formData, waste_amount_kg: e.target.value})}
                                            disabled={!canEdit}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Unit</InputLabel>
                                            <Select
                                                value={formData.waste_amount_unit}
                                                label="Unit"
                                                onChange={(e) => setFormData({...formData, waste_amount_unit: e.target.value})}
                                                disabled={!canEdit}
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }}
                                            >
                                                {availableUnits.map((unit) => (
                                                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label="Loss Reason"
                                    value={formData.loss_reason}
                                    onChange={(e) => setFormData({...formData, loss_reason: e.target.value})}
                                    disabled={!canEdit}
                                    placeholder="Explain any yield loss or waste..."
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Quality Parameters Card */}
                <Grid item xs={12} lg={6} sx={{ mt: 2 }}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Quality Parameters
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Final product specifications and characteristics
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Stack spacing={2.5}>
                                <TextField
                                    fullWidth
                                    label="Final pH"
                                    type="number"
                                    step="0.1"
                                    value={formData.final_ph}
                                    onChange={(e) => setFormData({...formData, final_ph: e.target.value})}
                                    disabled={!canEdit}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Final Viscosity (cP)"
                                    type="number"
                                    value={formData.final_viscosity}
                                    onChange={(e) => setFormData({...formData, final_viscosity: e.target.value})}
                                    disabled={!canEdit}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Final Color"
                                    value={formData.final_color}
                                    onChange={(e) => setFormData({...formData, final_color: e.target.value})}
                                    disabled={!canEdit}
                                    placeholder="e.g., Light pink, Clear, etc."
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Final Texture"
                                    value={formData.final_texture}
                                    onChange={(e) => setFormData({...formData, final_texture: e.target.value})}
                                    disabled={!canEdit}
                                    placeholder="e.g., Smooth, Creamy, etc."
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                                
                                <DateTimePicker
                                    label="End Time"
                                    value={formData.end_time}
                                    onChange={(newValue) => setFormData({...formData, end_time: newValue})}
                                    format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                                    ampm={batchTimeFormat !== '24hour'}
                                    disabled={!canEdit}
                                    renderInput={(params) => <TextField {...params} fullWidth 
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />}
                                />
                                
                                <Box sx={{ 
                                    p: 3,
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Overall Yield Efficiency (Calculated)
                                    </Typography>
                                    <Typography variant="h4" sx={{ 
                                        fontWeight: 500, 
                                        color: calculateYieldEfficiency() >= 90 ? '#4caf50' : calculateYieldEfficiency() >= 80 ? '#ff9800' : '#f44336'
                                    }}>
                                        {calculateYieldEfficiency()}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Based on yield (70%), waste efficiency (25%), and right-first-time (5% bonus)
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Yield Analysis Card */}
                <Grid item xs={12} lg={6} sx={{ mt: -20 }}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <TrendingUpIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Yield Analysis
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Waste categorization and recovery tracking
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Stack spacing={2.5}>
                                <Grid container spacing={1}>
                                    <Grid item xs={8}>
                                        <TextField
                                            fullWidth
                                            label="Recoverable Waste"
                                            type="number"
                                            step="0.01"
                                            value={formData.recoverable_waste_kg}
                                            onChange={(e) => setFormData({...formData, recoverable_waste_kg: e.target.value})}
                                            disabled={!canEdit}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Unit</InputLabel>
                                            <Select
                                                value={formData.recoverable_waste_unit}
                                                label="Unit"
                                                onChange={(e) => setFormData({...formData, recoverable_waste_unit: e.target.value})}
                                                disabled={!canEdit}
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }}
                                            >
                                                {availableUnits.map((unit) => (
                                                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                
                                <Grid container spacing={1}>
                                    <Grid item xs={8}>
                                        <TextField
                                            fullWidth
                                            label="Unrecoverable Waste"
                                            type="number"
                                            step="0.01"
                                            value={formData.unrecoverable_waste_kg}
                                            onChange={(e) => setFormData({...formData, unrecoverable_waste_kg: e.target.value})}
                                            disabled={!canEdit}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Unit</InputLabel>
                                            <Select
                                                value={formData.unrecoverable_waste_unit}
                                                label="Unit"
                                                onChange={(e) => setFormData({...formData, unrecoverable_waste_unit: e.target.value})}
                                                disabled={!canEdit}
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }}
                                            >
                                                {availableUnits.map((unit) => (
                                                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                
                                <TextField
                                    fullWidth
                                    label="Yield Loss Category"
                                    value={formData.yield_loss_category}
                                    onChange={(e) => setFormData({...formData, yield_loss_category: e.target.value})}
                                    disabled={!canEdit}
                                    placeholder="e.g., Equipment issue, Operator error, Material quality"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                                
                                                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.right_first_time}
                                                onChange={(e) => setFormData({...formData, right_first_time: e.target.checked})}
                                                disabled={!canEdit}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: theme.palette.primary.main,
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: theme.palette.primary.main,
                                                }
                                            }}
                                        />
                                    }
                                    label="Right First Time (No Rework Required)"
                                    sx={{ fontWeight: 500 }}
                                />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Notes and Comments Card */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backgroundColor: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Quality Notes & Comments
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Observations and additional information about the batch
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Quality Notes"
                                        value={formData.quality_notes}
                                        onChange={(e) => setFormData({...formData, quality_notes: e.target.value})}
                                        disabled={!canEdit}
                                        placeholder="Any quality observations, tests performed, or issues noted..."
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Yield Comments"
                                        value={formData.yield_comments}
                                        onChange={(e) => setFormData({...formData, yield_comments: e.target.value})}
                                        disabled={!canEdit}
                                        placeholder="Comments about yield performance, process improvements, etc..."
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<CheckCircleIcon />}
                            size="large"
                            disabled={!canEdit || loading}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                color: canEdit ? theme.palette.text.secondary : theme.palette.grey[100],
                                fontWeight: 500,
                                background: canEdit ? 'rgba(255, 255, 255, 0.95)' : theme.palette.grey[400],
                                border: canEdit ? `1px solid ${alpha(theme.palette.grey[300], 0.8)}` : 'none',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    backgroundColor: canEdit ? alpha(theme.palette.primary.main, 0.04) : theme.palette.grey[400]
                                },
                                '&:disabled': {
                                    opacity: 0.6,
                                    background: theme.palette.grey[400],
                                    color: theme.palette.grey[100]
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {!canEdit ? 'Read Only - No Edit Permissions' : (loading ? 'Saving...' : 'Save Yield Summary')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            </Box>
        </LocalizationProvider>
    );
} 